import type Quill from 'quill/core';
import { useCallback, useEffect } from 'react';
import { useEffectEvent } from 'use-effect-event';
import type { MagneticWallFocus, MagneticWallHighlight } from './editorSlice';
import { getScrollDistanceToThreadContext } from '../post/comment/getMagneticWallItemLayout';
import { getScrollContainer } from '../post/listenToPostScroll';
import type { MagneticWallAttributor } from './formats/utils/attributors';
import { getAttributorFromMagneticWallType } from './formats/utils/attributors';
import { useQuill } from './QuillContext';
import { getHeaderCollapse } from './useHeaderCollapse';
import { getListCollapse } from './useListCollapse';
import usePerformLoadSafeScrolling from '../post/utils/usePerformLoadSafeScrolling';

export function removeStatuses(quill: Quill, statuses: string[]) {
  const selector = statuses.map((status) => `[class*="-${status}"]`).join(', ');

  quill.root.querySelectorAll(selector).forEach((el) => {
    const classNamesWithPrefix = Array.from(el.classList).filter((className) =>
      statuses.some((status) => className.endsWith(`-${status}`)),
    );
    el.classList.remove(...classNamesWithPrefix);
  });
}

function addClassNameBySelector(
  quill: Quill,
  selector: string,
  className: string,
) {
  quill.root.querySelectorAll(selector).forEach((el) => {
    el.classList.add(className);
  });
}

export function addStatusByAttributor(
  quill: Quill,
  attributor: MagneticWallAttributor,
  id: string,
  status: string,
) {
  addClassNameBySelector(
    quill,
    `[class*="${attributor.keyName}-${id}"]`,
    `status-${attributor.keyName}-${status}`,
  );
}

interface UseSyncMagneticWallStatesArgs {
  magneticWallFocus: MagneticWallFocus | null;
  magneticWallHighlight: MagneticWallHighlight | null;
}

const useSyncMagneticWallStates = ({
  magneticWallFocus,
  magneticWallHighlight,
}: UseSyncMagneticWallStatesArgs) => {
  const { quill } = useQuill();

  const expandCollapsedHeadersIfNecessary = useCallback(
    (activeItem: HTMLElement) => {
      if (!quill) return;

      const blot = quill.scroll.find(activeItem, true);
      if (blot) {
        getHeaderCollapse(quill)?.revealBlot(blot);
        getListCollapse(quill)?.revealBlot(blot);
      }
    },
    [quill],
  );

  const performLoadSafeScrolling = useEffectEvent(
    usePerformLoadSafeScrolling(),
  );
  useEffect(() => {
    if (!quill) return undefined;

    removeStatuses(quill, ['focused', 'active', 'highlight']);

    if (magneticWallFocus) {
      const status = magneticWallFocus.active ? 'active' : 'focused';
      const attributor = getAttributorFromMagneticWallType(
        magneticWallFocus.type,
      );
      addStatusByAttributor(quill, attributor, magneticWallFocus.id, status);
    }

    const activeItem = magneticWallFocus?.active
      ? quill.root.querySelector<HTMLElement>(`[class*="-active"]`)
      : null;
    if (!activeItem) return undefined;

    expandCollapsedHeadersIfNecessary(activeItem);

    const scrollingContainer = getScrollContainer();
    if (!scrollingContainer) return undefined;

    return performLoadSafeScrolling(quill, (hasUserInteracted) => {
      const distance = getScrollDistanceToThreadContext(
        scrollingContainer.getBoundingClientRect(),
        activeItem.getBoundingClientRect(),
      );
      if (distance !== 0) {
        scrollingContainer.scrollBy({
          top: distance,
          behavior: hasUserInteracted ? 'smooth' : 'instant',
        });
      }
    });
  }, [quill, magneticWallFocus, expandCollapsedHeadersIfNecessary]);

  useEffect(() => {
    if (!quill) return;

    removeStatuses(quill, ['highlight']);

    if (magneticWallHighlight) {
      const attributor = getAttributorFromMagneticWallType(
        magneticWallHighlight.type,
      );

      addStatusByAttributor(
        quill,
        attributor,
        magneticWallHighlight.id,
        'highlight',
      );
    }
  }, [quill, magneticWallHighlight]);
};

export default useSyncMagneticWallStates;
